import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Star, Timer, Trophy, RotateCcw, ArrowLeft, Rocket, Loader2, Sparkles } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { LAB_META, fmtTime, saveMission, starsFor } from "@/lib/labs";
import { indiaDate } from "@/lib/classroom_live";

const Flight = lazy(() => import("@/components/lab3d/missions/FlightMission"));
const MISSIONS = {
  rover: lazy(() => import("@/components/lab3d/missions/RoverMission")),
  linefollower: lazy(() => import("@/components/lab3d/missions/LineMission")),
  arm: lazy(() => import("@/components/lab3d/missions/ArmMission")),
  drone: Flight, racedrone: Flight, deliverydrone: Flight, rescuedrone: Flight,
  walker: lazy(() => import("@/components/lab3d/missions/WalkMission")),
  dancer: lazy(() => import("@/components/lab3d/missions/DanceMission")),
};

export const Stars = ({ n, size = "w-5 h-5", testid }) => (
  <span className="inline-flex gap-0.5" data-testid={testid} data-stars={n}>
    {[1, 2, 3].map((i) => <Star key={i} className={`${size} ${i <= n ? "fill-[#FFC94D] text-[#FFC94D]" : "text-slate-500"}`} />)}
  </span>
);

// Shows the mission's own simulated clock when it reports one (flight/rover), else wall-clock time. Penalties show separately.
const LiveTimer = ({ t0, on, clock }) => {
  const el = useRef(null);
  useEffect(() => {
    if (!on) return undefined;
    let raf;
    const tick = () => { if (el.current) el.current.textContent = fmtTime(clock.current ?? (performance.now() - t0.current) / 1000); raf = requestAnimationFrame(tick); };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [on, t0, clock]);
  return <span ref={el} data-testid="mission-timer">0.0s</span>;
};

// Runs one lab mission: intro → 3-2-1 → play (timer) → stars + best time saved to the portfolio.
export const MissionHost = ({ lab, parts, best, lowbw, fallback, layout, onExit, onSaved }) => {
  const { L } = useLang();
  const meta = LAB_META[lab.id];
  const sim = meta.clock === "sim";
  const Mission = MISSIONS[lab.id];
  const [stage, setStage] = useState("intro");
  const [count, setCount] = useState(3);
  const [penalty, setPenalty] = useState(0);
  const [result, setResult] = useState(null);
  const [run, setRun] = useState(0);
  const t0 = useRef(0);
  const pen = useRef(0);
  const won = useRef(false);
  const clock = useRef(null);
  useEffect(() => {
    if (stage !== "count") return undefined;
    if (count === 0) { t0.current = performance.now(); setStage("run"); return undefined; }
    const t = setTimeout(() => setCount((c) => c - 1), 650);
    return () => clearTimeout(t);
  }, [stage, count]);
  const start = () => { pen.current = 0; won.current = false; clock.current = null; setPenalty(0); setResult(null); if (sim) setStage("run"); else { setCount(3); setStage("count"); } };
  const again = () => { setRun((n) => n + 1); start(); };
  const addPenalty = useCallback((s) => { pen.current += s; setPenalty(pen.current); }, []);
  const win = useCallback(async (simT) => {
    if (won.current) return;
    won.current = true;
    const t = Math.round(((typeof simT === "number" ? simT : (performance.now() - t0.current) / 1000) + pen.current) * 10) / 10;
    setStage("won");
    setResult({ time_s: t, stars: starsFor(lab.mission, t), saving: true });
    const r = await saveMission(lab.id, lab.mission, t, best);
    setResult({ ...r, time_s: r.time_s ?? t });
    onSaved?.();
  }, [lab, best, onSaved]);

  const m = lab.mission;
  const hud = (
    <>
      <div className="absolute top-3 left-3 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/55 backdrop-blur text-xs font-bold text-white" data-testid="mission-hud">
        <Rocket className="w-3.5 h-3.5 text-[#00E5FF]" />{L(...meta.mission)}
        {!sim && stage === "run" && <><Timer className="w-3.5 h-3.5 text-[#FFC94D]" /><LiveTimer t0={t0} on clock={clock} /></>}
        {penalty > 0 && <span className="text-[#FF8FA8]" data-testid="mission-penalty">+{penalty}s</span>}
      </div>
      {stage === "count" && <div className="absolute inset-0 grid place-items-center pointer-events-none"><span key={count} className="text-8xl font-black text-white drop-shadow-[0_0_30px_rgba(0,229,255,0.9)] animate-ping-once" data-testid="mission-countdown">{count || L("GO!", "चलो!")}</span></div>}
      {stage === "won" && result && (
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 mx-auto w-fit px-6 py-4 rounded-3xl bg-[#0A192F]/85 backdrop-blur border border-[#00E5FF]/40 text-center text-white" data-testid="mission-win-banner">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#00E5FF]">{L("Mission complete", "मिशन पूरा")}</div>
          <div className="mt-2"><Stars n={result.stars} size="w-9 h-9" /></div>
          <div className="mt-1 text-2xl font-black">{fmtTime(result.time_s)}</div>
        </div>
      )}
    </>
  );
  const panel = (
    <div className="rounded-3xl bg-[#0A192F] text-white p-4 border border-[#16335C]" data-testid="mission-panel">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#00E5FF]">{L("Mission", "मिशन")} · <span className="text-[#FFC94D]">{L("Free", "फ़्री")}</span></span>
        <span className="text-[11px] text-slate-300 inline-flex items-center gap-1" data-testid="mission-best"><Trophy className="w-3.5 h-3.5 text-[#FFC94D]" />{L("Best", "बेस्ट")}: {fmtTime(best?.mission_best ?? null)}{best?.mission_stars ? <Stars n={best.mission_stars} size="w-3 h-3" /> : null}</span>
      </div>
      <div className="mt-1 text-xl font-extrabold" data-testid="mission-title">{L(...meta.mission)}</div>
      <p className="mt-1 text-sm text-slate-300" data-testid="mission-goal">{L(...meta.goal)}</p>
      <div className="mt-3 grid grid-cols-3 gap-1.5 text-[11px] font-bold text-center" data-testid="mission-thresholds">
        <span className="rounded-xl bg-white/5 py-1.5"><Stars n={3} size="w-3 h-3" /><br />≤ {fmtTime(m.t3)}</span>
        <span className="rounded-xl bg-white/5 py-1.5"><Stars n={2} size="w-3 h-3" /><br />≤ {fmtTime(m.t2)}</span>
        <span className="rounded-xl bg-white/5 py-1.5"><Stars n={1} size="w-3 h-3" /><br />{L("finish", "पूरा करें")}</span>
      </div>
      {stage === "intro" && (
        <button onClick={start} data-testid="mission-start-btn" className="mt-3 w-full h-12 rounded-full bg-[#00E5FF] text-[#0A192F] font-extrabold inline-flex items-center justify-center gap-2 hover:bg-white transition-colors">
          <Rocket className="w-5 h-5" />{L("Start mission", "मिशन शुरू करें")}</button>
      )}
      {stage === "won" && result && (
        <div className="mt-3 rounded-2xl bg-white/5 p-3" data-testid="mission-result">
          <div className="flex items-center justify-between"><Stars n={result.stars} testid="mission-result-stars" /><span className="text-lg font-black" data-testid="mission-result-time">{fmtTime(result.time_s)}</span></div>
          {result.saving ? <p className="mt-1 text-[11px] text-slate-400 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />{L("Saving…", "सेव हो रहा है…")}</p>
            : <p className="mt-1 text-xs text-slate-300">{result.new_best && <span className="mr-1 inline-flex items-center gap-1 font-bold text-[#FFC94D]" data-testid="mission-new-best"><Sparkles className="w-3.5 h-3.5" />{L("New best time!", "नया बेस्ट टाइम!")}</span>}
              {L(`Best ${fmtTime(result.best)} · badge stars ${result.best_stars}/3`, `बेस्ट ${fmtTime(result.best)} · बैज स्टार ${result.best_stars}/3`)}{penalty > 0 && L(` · includes +${penalty}s penalty`, ` · +${penalty}s पेनल्टी शामिल`)}</p>}
          {result.failed && <p className="mt-1 text-[11px] font-semibold text-[#FF8FA8]" data-testid="mission-save-failed">{L("Couldn't save to your portfolio (internet?). Play again to save.", "पोर्टफ़ोलियो में सेव नहीं हुआ (इंटरनेट?)। सेव करने के लिए फिर खेलिए।")}</p>}
          <div className="mt-3 flex gap-2">
            <button onClick={again} data-testid="mission-retry-btn" className="flex-1 h-11 rounded-full bg-[#FF3B3B] font-bold text-sm inline-flex items-center justify-center gap-1.5"><RotateCcw className="w-4 h-4" />{L("Play again", "फिर खेलें")}</button>
            <button onClick={onExit} data-testid="mission-done-btn" className="flex-1 h-11 rounded-full bg-white text-[#0A192F] font-bold text-sm">{L("Done", "हो गया")}</button>
          </div>
        </div>
      )}
      {stage !== "won" && <button onClick={onExit} data-testid="mission-exit-btn" className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-300 hover:text-white"><ArrowLeft className="w-4 h-4" />{L("Back to the lab", "लैब पर वापस")}</button>}
    </div>
  );
  const loading = <div className="absolute inset-0 grid place-items-center"><Loader2 className="w-7 h-7 animate-spin text-[#00E5FF]" /></div>;
  return (
    <Suspense fallback={layout(loading, panel)}>
      <Mission key={run} lab={lab.id} parts={parts} lowbw={lowbw} fallback={fallback} running={stage === "run"} onWin={win} onPenalty={addPenalty} clock={clock} date={indiaDate()}
        render={(scene, controls) => layout(<>{scene}{hud}</>, <>{stage === "run" && controls}{panel}</>)} />
    </Suspense>
  );
};
